import { useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Add, Archive, Dot } from '@mint-lib/icons';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  Skeleton,
  typographyVariants,
} from '@mint-lib/tw-components';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

import { useCreateFlightVersion } from '../../../../../../api/mutations/useCreateFlightVersion.js';
import { useChosenLevel } from '../../../../../../hooks/useChosenLevel.js';
import { VersionCard } from './VersionCard.jsx';

type VersioningDrawerContentProps = {
  onOpenChange: (isOpen: boolean) => void;
  flightVersions: MediaPlan.FlightVersionItem[];
  isVersionListFetching: boolean;
};

export const VersioningDrawerContent = ({
  onOpenChange,
  flightVersions,
  isVersionListFetching,
}: VersioningDrawerContentProps) => {
  const { t } = useTranslation('@myn/planning_v2');

  const { id: mediaDataId } = useChosenLevel();

  const isReadOnly = !useIsAllowedPermission('campaignPlanningFeature:modify');

  const {
    mutateAsync: createFlightVersionAsync,
    isPending: isCreateVersionPending,
  } = useCreateFlightVersion();

  const handleCreateNewVersion = () => {
    if (!mediaDataId) return;

    createFlightVersionAsync({ mediaDataId });
  };

  const EMPTY_VERSION = {
    id: null,
    name: t('No version'),
    createdAt: null,
  };

  const archivedVersions = useMemo(
    () => flightVersions.filter((version) => !version.isCurrent).reverse(),
    [flightVersions],
  );

  // clarify with business what should be shown if no versions present
  const currentVersion =
    flightVersions.find((version) => version.isCurrent) ?? EMPTY_VERSION;

  return (
    <div className="flex w-full flex-col gap-6">
      <Button
        variant="text"
        color="primary"
        size="medium"
        onClick={handleCreateNewVersion}
        disabled={isCreateVersionPending || isReadOnly}
        data-dd-action-name="Versioning drawer: create new version"
      >
        <Add />
        {t('Create new version')}
      </Button>

      <div className="flex flex-col">
        <div className="flex items-center">
          <h5 className={typographyVariants({ variant: 'h5' })}>
            {t('Current version')}
          </h5>
          <Dot className="mr-2 fill-success-main" />
        </div>

        {isVersionListFetching || isCreateVersionPending ? (
          <Skeleton className="my-2 h-19 w-full" />
        ) : (
          <VersionCard
            id={currentVersion.id}
            name={currentVersion.name}
            createdAt={currentVersion.createdAt}
            isCurrent
            onOpenChange={onOpenChange}
          />
        )}
      </div>

      <Accordion type="single" collapsible className="w-full">
        <AccordionItem variant="primary" value="archived">
          <AccordionTrigger>
            <div className="flex w-full gap-1">
              <h6 className={typographyVariants({ variant: 'h6' })}>
                {t('Archived')}
              </h6>
              <Archive />
            </div>
          </AccordionTrigger>
          <AccordionContent>
            <div className="max-h-[60vh] overflow-y-auto">
              {archivedVersions.map((version) => (
                <VersionCard
                  key={version.id ?? version.index}
                  id={version.id}
                  name={version.name}
                  createdAt={version.createdAt}
                  isCurrent={version.isCurrent}
                  onOpenChange={onOpenChange}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};
